import { create } from 'zustand';

// Import the default config
import defaultConfig from '../config/images.default.json';

// ============================================================================
// Image Config Store
// Manages image configuration for CDN-ready dynamic image URLs
// ============================================================================

// Types
export type ImageRole = 'hero' | 'card' | 'product' | 'background' | 'logo' | 'placeholder' | 'icon';

export interface ImageEntry {
  path: string;
  alt: string;
  role?: ImageRole;
}

export interface ImageConfig {
  version: string;
  baseUrl: string;
  images: Record<string, ImageEntry>;
}

export type ImageConfigStatus = 'idle' | 'loading' | 'ready' | 'error';

// State interface
interface ImageConfigState {
  config: ImageConfig | null;
  status: ImageConfigStatus;
  error: string | null;
}

// Actions interface
interface ImageConfigActions {
  /** Load config from default JSON (called on app init) */
  loadConfig: () => void;
  /** Load config from a custom source (for franchise overrides) */
  loadCustomConfig: (config: ImageConfig) => void;
  /** Get an image entry by key */
  getImage: (key: string) => ImageEntry | undefined;
  /** Get the full URL for an image key (baseUrl + path) */
  getUrl: (key: string) => string | undefined;
  /** Get the current base URL */
  getBaseUrl: () => string;
}

type ImageConfigStore = ImageConfigState & ImageConfigActions;

// Initial state
const initialState: ImageConfigState = {
  config: null,
  status: 'idle',
  error: null,
};

// Validate config structure
function isValidImageConfig(obj: unknown): obj is ImageConfig {
  if (!obj || typeof obj !== 'object') return false;
  const config = obj as Record<string, unknown>;

  if (typeof config.version !== 'string') return false;
  if (typeof config.baseUrl !== 'string') return false;
  if (!config.images || typeof config.images !== 'object') return false;

  return true;
}

// Create the store (no persistence needed - config is static)
export const useImageConfigStore = create<ImageConfigStore>()((set, get) => ({
  ...initialState,

  loadConfig: () => {
    set({ status: 'loading', error: null });

    try {
      if (!isValidImageConfig(defaultConfig)) {
        throw new Error('Invalid image configuration format');
      }

      set({ config: defaultConfig as ImageConfig, status: 'ready', error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load image config';
      set({ status: 'error', error: message });

      if (import.meta.env.DEV) {
        console.error('[ImageConfigStore] Failed to load config:', err);
      }
    }
  },

  loadCustomConfig: (config: ImageConfig) => {
    set({ status: 'loading', error: null });

    try {
      if (!isValidImageConfig(config)) {
        throw new Error('Invalid image configuration format');
      }

      set({ config, status: 'ready', error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load custom config';
      set({ status: 'error', error: message });

      if (import.meta.env.DEV) {
        console.error('[ImageConfigStore] Failed to load custom config:', err);
      }
    }
  },

  getImage: (key: string) => {
    const { config } = get();
    if (!config) return undefined;
    return config.images[key];
  },

  getUrl: (key: string) => {
    const { config } = get();
    if (!config) return undefined;

    const entry = config.images[key];
    if (!entry) return undefined;

    // baseUrl has no trailing slash, path starts with /
    return `${config.baseUrl}${entry.path}`;
  },

  getBaseUrl: () => {
    const { config } = get();
    return config?.baseUrl ?? '';
  },
}));

// ============================================================================
// Selectors for optimized re-renders
// ============================================================================

export const selectImageConfigStatus = (state: ImageConfigStore) => state.status;
export const selectImageConfigError = (state: ImageConfigStore) => state.error;
export const selectImageConfigVersion = (state: ImageConfigStore) => state.config?.version ?? '';
export const selectBaseUrl = (state: ImageConfigStore) => state.config?.baseUrl ?? '';

// ============================================================================
// Hook for single image (with dev warning)
// ============================================================================

/**
 * Get a single image URL and entry from the store.
 * Logs a warning in development if the key is not found.
 */
export function getImageWithWarning(key: string): { url: string | undefined; entry: ImageEntry | undefined } {
  const store = useImageConfigStore.getState();
  const entry = store.getImage(key);
  const url = store.getUrl(key);

  if (store.status === 'ready' && !entry && import.meta.env.DEV) {
    console.warn(`[ImageConfigStore] Image key not found: "${key}"`);
  }

  return { url, entry };
}
